/**
 * ConnectedSteps — a horizontal row of step cards joined by a connecting line
 * that draws itself as you scroll; each card lights up in turn as the line
 * reaches it. Every card leaves room for a square photo at the bottom. Stacks to
 * one column on mobile (line hidden). Content-agnostic. No raw [#hex]; named
 * tokens only (note: avoid `/NN` opacity on ds tokens — it renders transparent).
 */
import { useRef, useState, type ReactNode } from "react";
import { m, useScroll, useTransform, useMotionValueEvent } from "motion/react";
import { MotionProvider } from "./motion";
import { CardHeading } from "./Heading";

export type ConnectedStep = { title: ReactNode; body: ReactNode; image?: string; alt?: string };

export function ConnectedSteps({
  eyebrow,
  title,
  description,
  actions,
  steps,
}: {
  eyebrow?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  steps: ConnectedStep[];
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.8", "end 0.6"] });
  const fill = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const [active, setActive] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(steps.filter((_, i) => v >= (i + 0.45) / steps.length).length);
  });

  return (
    <MotionProvider>
      <section className="bg-bg font-ds">
        <div className="mx-auto max-w-content px-4 py-16 md:px-10 md:py-20">
          {eyebrow}
          {title && (
            <h2 className="mt-5 max-w-[640px] text-balance text-ds-30 font-semibold leading-[1.12] tracking-[-0.5px] text-ds-ink lg:text-ds-34">
              {title}
            </h2>
          )}
          {description && (
            <p className="mt-4 max-w-[560px] text-ds-18 leading-relaxed text-ds-ink-soft">{description}</p>
          )}
          {actions && <div className="mt-7 flex flex-wrap items-center gap-3">{actions}</div>}

          <div ref={ref} className="relative mt-12 grid gap-6 lg:grid-cols-3">
            {/* connecting line — desktop only; a dashed line through the vertical
                centre, behind the cards (visible in the gaps). The blue dashes fill
                in left→right with scroll as each card lights up. */}
            <div
              className="pointer-events-none absolute inset-x-0 top-1/2 hidden h-0.5 -translate-y-1/2 lg:block"
              style={{ background: "repeating-linear-gradient(to right, var(--line) 0 7px, transparent 7px 14px)" }}
            >
              <m.div
                className="absolute inset-y-0 left-0"
                style={{ width: fill, background: "repeating-linear-gradient(to right, var(--ds-accent) 0 7px, transparent 7px 14px)" }}
              />
            </div>

            {steps.map((s, i) => {
              const on = i < active;
              return (
                <div
                  key={i}
                  className={`relative z-10 flex flex-col overflow-hidden rounded-3xl border-8 border-white p-4 md:p-7 shadow-[0_1px_3px_rgba(15,23,42,0.05)] transition-colors duration-500 ${
                    on ? "bg-white" : "bg-bg-subtle"
                  }`}
                >
                  <span
                    className={`grid h-12 w-12 place-items-center rounded-full text-ds-18 font-bold transition-colors duration-500 ${
                      on ? "bg-ds-accent text-white" : "bg-white text-ds-ink-soft ring-1 ring-line"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <div className="mt-5"><CardHeading title={s.title} body={s.body} /></div>

                  {/* 4:3 photo — full-bleed to the card edges (negates the p-4 md:p-7
                      padding), framed by the white border; clipped by the card's
                      rounded-3xl + overflow-hidden. Omitted entirely when no image. */}
                  {s.image && (
                    <img src={s.image} alt={s.alt || ""} loading="lazy" decoding="async" className="mt-auto pt-6 -mx-4 -mb-4 md:-mx-7 md:-mb-7 block aspect-[4/3] w-[calc(100%+2rem)] md:w-[calc(100%+3.5rem)] max-w-none object-cover" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </MotionProvider>
  );
}
